import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import { executeQuery, QueryResult } from '../services/api';

export default function QueryInterface() {
  const [sql, setSql] = useState('SELECT * FROM users LIMIT 10;');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExecute = async () => {
    if (!sql.trim()) return;

    setLoading(true);
    setError(null);
    
    try {
      const queryResult = await executeQuery(sql);
      setResult(queryResult);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to execute query');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      handleExecute();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [sql]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        SQL Query Interface
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            SQL Editor
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Press Ctrl+Enter (Cmd+Enter on Mac) to execute query
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <AceEditor
            mode="sql"
            theme="github"
            value={sql}
            onChange={setSql}
            name="sql-editor"
            editorProps={{ $blockScrolling: true }}
            width="100%"
            height="200px"
            fontSize={14}
            showPrintMargin={false}
            showGutter={true}
            highlightActiveLine={true}
            setOptions={{
              enableBasicAutocompletion: true,
              enableLiveAutocompletion: true,
              enableSnippets: true,
              showLineNumbers: true,
              tabSize: 2,
            }}
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleExecute}
          disabled={loading || !sql.trim()}
          sx={{ mr: 2 }}
        >
          {loading ? <CircularProgress size={20} /> : 'Execute Query'}
        </Button>

        <Button
          variant="outlined"
          onClick={() => setSql('')}
          disabled={loading}
        >
          Clear
        </Button>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <Paper sx={{ p: 2, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6">Query Results</Typography>
            <Chip 
              label={`${result.row_count} rows`} 
              size="small" 
              color="primary" 
            />
            <Chip 
              label={`${result.execution_time.toFixed(3)}s`} 
              size="small" 
              color="secondary" 
            />
          </Box>

          {result.rows.length > 0 ? (
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {result.columns.map((column, index) => (
                      <TableCell key={index} sx={{ fontWeight: 'bold' }}>
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>
                          {cell !== null ? String(cell) : <em style={{ color: '#999' }}>NULL</em>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" color="text.secondary">
              Query executed successfully. No rows returned.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}